/**
 * A recuperação da senha (docs/spec/email.md). "Esqueci a senha", na entrada, pede o link pelo nome da
 * conta ou pelo e-mail; o link do e-mail abre o diálogo da senha nova, na entrada ou por cima do app.
 * Sem o endereço do site no servidor, o "Esqueci a senha" manda pedir ao administrador, como antes.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api.js';
import { useLanguage, useT } from '../i18n/index.js';
import { PASSWORD_RESET_LIMITS } from '../shared.js';
import { useApp } from '../store.js';
import { Button, Dialog, ErrorLabel, TextEdit } from '../ui/controls.js';
import { MARK, emphasize } from '../ui/emphasize.js';
import './passwordReset.css';

const RESET_MINUTES = Math.round(PASSWORD_RESET_LIMITS.linkMs / 60_000);
/** a regra do servidor (src/auth.ts): o botão só acende com ela, e o servidor confere de novo */
const PASSWORD_MIN_LENGTH = 8;

type ForgotPhase = 'loading' | 'form' | 'sent' | 'unavailable' | 'offline';

/** "Esqueci a senha": o pedido do link. `initial`: o que estava escrito no campo da conta. */
export function ForgotPasswordDialog({ initial, onClose }: { initial: string; onClose: () => void }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const [phase, setPhase] = useState<ForgotPhase>('loading');
  const [account, setAccount] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // o servidor diz se manda o link: sem o endereço do site, vale o aviso de pedir ao administrador
  useEffect(() => {
    let alive = true;
    api.forgotPasswordAvailable().then(
      ({ available }) => {
        if (alive) setPhase(available ? 'form' : 'unavailable');
      },
      () => {
        if (alive) setPhase('offline');
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  async function submit() {
    const clean = account.trim();
    if (!clean) {
      setError(t('login.error.accountRequired'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.forgotPassword(clean, language);
      setAccount(clean);
      setPhase('sent');
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : t('login.connectionProblem'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={t('forgot.title')}
      onClose={onClose}
      actions={
        phase === 'form' ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t('create.cancel')}
            </Button>
            <Button variant="action" busy={busy} onClick={() => void submit()}>
              {t('forgot.submit')}
            </Button>
          </>
        ) : phase === 'loading' ? (
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
        ) : (
          <Button variant="action" onClick={onClose}>
            {t('common.ok')}
          </Button>
        )
      }
    >
      {phase === 'loading' ? (
        <p className="rd-recover__text" role="status">
          {t('forgot.checking')}
        </p>
      ) : null}
      {phase === 'unavailable' ? <p className="rd-recover__text">{t('forgot.text')}</p> : null}
      {phase === 'offline' ? <p className="rd-recover__text">{t('forgot.offline')}</p> : null}
      {phase === 'form' ? (
        <form
          className="rd-recover"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <p className="rd-recover__text">{t('forgot.intro')}</p>
          <label className="rd-recover__field">
            <span>{t('login.account')}</span>
            <TextEdit look="filled" value={account} onChange={setAccount} autoComplete="username" invalid={!!error} autoFocusOnMount />
          </label>
          {error ? <ErrorLabel>{error}</ErrorLabel> : null}
          <button type="submit" hidden />
        </form>
      ) : null}
      {phase === 'sent' ? (
        <div className="rd-recover" role="status">
          <p className="rd-recover__text">{emphasize(t('forgot.sent', { account: MARK, minutes: RESET_MINUTES }), account)}</p>
          <p className="rd-recover__text">{t('forgot.spam')}</p>
          <p className="rd-recover__note">{t('forgot.noEmail')}</p>
        </div>
      ) : null}
    </Dialog>
  );
}

/** O diálogo da senha nova, aberto pelo link da recuperação (o código fica na store até fechar). */
export function PasswordResetDialog() {
  const code = useApp((s) => s.passwordResetCode);
  return code ? <ResetDialog key={code} code={code} /> : null;
}

type ResetPhase = 'checking' | 'form' | 'done' | 'expired' | 'invalid' | 'offline';

function ResetDialog({ code }: { code: string }) {
  const t = useT();
  const close = useApp((s) => s.closePasswordReset);
  const reveal = { show: t('login.showPassword'), hide: t('login.hidePassword') };
  const [phase, setPhase] = useState<ResetPhase>('checking');
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // antes de pedir a senha: de que conta é o link, e se ele ainda vale
  useEffect(() => {
    let alive = true;
    api.checkPasswordReset(code).then(
      ({ displayName }) => {
        if (!alive) return;
        setAccount(displayName);
        setPhase('form');
      },
      (problem: unknown) => {
        if (!alive) return;
        const status = problem instanceof ApiError ? problem.status : 0;
        setPhase(status === 410 ? 'expired' : status === 404 || status === 400 ? 'invalid' : 'offline');
      },
    );
    return () => {
      alive = false;
    };
  }, [code]);

  const mismatch = again !== '' && password !== again;
  const ready = password.length >= PASSWORD_MIN_LENGTH && password === again;

  async function submit() {
    if (!ready) return;
    setBusy(true);
    setError('');
    try {
      const { displayName } = await api.resetPassword(code, password);
      setAccount(displayName);
      setPhase('done');
    } catch (problem) {
      const status = problem instanceof ApiError ? problem.status : 0;
      if (status === 410) setPhase('expired');
      else if (status === 404) setPhase('invalid');
      else setError(problem instanceof ApiError ? problem.message : t('login.connectionProblem'));
    } finally {
      setBusy(false);
    }
  }

  // trocada: o formulário de entrar vem com a conta (e a aba sai dela, se estava nela)
  const finish = () => close(phase === 'done' ? account : undefined);
  const title = phase === 'done' ? t('reset.done.title') : phase === 'expired' ? t('reset.expired.title') : phase === 'invalid' ? t('reset.invalid.title') : t('reset.title');

  return (
    <Dialog
      title={title}
      onClose={finish}
      actions={
        phase === 'form' ? (
          <>
            <Button variant="secondary" onClick={finish}>
              {t('create.cancel')}
            </Button>
            <Button variant="action" disabled={!ready} busy={busy} onClick={() => void submit()}>
              {t('reset.submit')}
            </Button>
          </>
        ) : phase === 'checking' ? (
          <Button variant="secondary" onClick={finish}>
            {t('create.cancel')}
          </Button>
        ) : (
          <Button variant="action" onClick={finish}>
            {t('common.ok')}
          </Button>
        )
      }
    >
      {phase === 'checking' ? (
        <p className="rd-recover__text" role="status">
          {t('reset.checking')}
        </p>
      ) : null}
      {phase === 'form' ? (
        <form
          className="rd-recover"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <p className="rd-recover__text">{emphasize(t('reset.for', { account: MARK }), account)}</p>
          {/* o nome da conta, escondido: o gerenciador de senhas guarda a nova na conta certa */}
          <input type="text" name="username" autoComplete="username" value={account} readOnly hidden />
          <label className="rd-recover__field">
            <span>{t('reset.password')}</span>
            <TextEdit look="filled" type="password" reveal={reveal} value={password} onChange={setPassword} autoComplete="new-password" autoFocusOnMount />
          </label>
          <label className="rd-recover__field">
            <span>{t('reset.again')}</span>
            <TextEdit look="filled" type="password" reveal={reveal} value={again} onChange={setAgain} autoComplete="new-password" invalid={mismatch} />
          </label>
          <p className="rd-recover__note">{mismatch ? t('config.account.mismatch') : t('reset.rule')}</p>
          {error ? <ErrorLabel>{error}</ErrorLabel> : null}
          <button type="submit" hidden />
        </form>
      ) : null}
      {phase === 'done' ? (
        <p className="rd-recover__text" role="status">
          {emphasize(t('reset.done', { account: MARK }), account)}
        </p>
      ) : null}
      {phase === 'expired' ? <p className="rd-recover__text">{t('reset.expired')}</p> : null}
      {phase === 'invalid' ? <p className="rd-recover__text">{t('reset.invalid')}</p> : null}
      {phase === 'offline' ? <p className="rd-recover__text">{t('reset.offline')}</p> : null}
    </Dialog>
  );
}
